import { Component, EventEmitter, Output, signal } from '@angular/core';
import { StrideReports } from '../../../../../services/stride-reports';
import { Client, StrideSchoolReportData, StrideSchoolReportFilter, StrideSchoolSectionData } from '@mira-avant/mtlibs';
import { StrideSchoolReportSummary } from './stride-school-report-summary/stride-school-report-summary';
import { Column, ReportingTable } from '../../reporting-table/reporting-table';
import { HeaderColumn } from '../../reporting-table/header-column';
import { RowColumn } from '../../reporting-table/row-column';
import { ClientService } from '../../../../../services/client.service';
import { CommonModule } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NavigationService } from '../../../../../services/navigation.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { DisplayNamePipe } from '../../../../../pipes/display-name.pipe';
import { SectionSkillDistributionBar } from './section-skill-distribution-bar/section-skill-distribution-bar';
import { BrowserService } from '../../../../../services/browser.service';

const FULL_WIDTH_BREAKPOINT = 1100;

@Component({
	selector: 'app-stride-school-report',
	imports: [
		CommonModule,
		StrideSchoolReportSummary,
		ReportingTable,
		RowColumn,
		HeaderColumn,
		NzIconModule,
		DisplayNamePipe,
		SectionSkillDistributionBar,
	],
	standalone: true,
	templateUrl: './stride-school-report.html',
	styleUrl: './stride-school-report.scss',
})
export class StrideSchoolReport {
	@Output('onSelectSection') public onSelectSection = new EventEmitter<string>();

	public report = signal<StrideSchoolReportData | undefined>(undefined);
	public loading = signal<boolean>(true);
	public rows = signal<StrideSchoolSectionData[]>([]);
	public columns = signal<Column[]>([]);
	public client = signal<Client | undefined>(undefined);
	private sub?: Subscription;
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];

	constructor(
		private strideReportsService: StrideReports,
		private clientService: ClientService,
		private nav: NavigationService,
		private browserService: BrowserService
	) {}

	ngOnInit() {
		this.reload();

		this.clientService.getClientBySubDomain$().then(client => {
			this.client.set(client);
		});
	}

	public async reload(): Promise<void> {
		const matrixParams = await firstValueFrom(this.nav.matrixParams$);
		const locationUuid = matrixParams['locationUuid'];

		await this.updateReport({
			locationUuid: locationUuid,
		});
	}

	public async updateReport(filter: StrideSchoolReportFilter): Promise<void> {
		this.loading.set(true);

		try {
			const params = await firstValueFrom(this.nav.matrixParams$);

			if (!params['product']) {
				throw new Error('Product parameter is missing');
			}

			const reportData = await this.strideReportsService.getStrideSchoolReport$(params['product'], filter);
			this.report.set(reportData);
			this.rows.set(reportData.sections);

			this.updateTableDetails();

			this.sub?.unsubscribe();
			this.sub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error fetching school report:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private updateTableDetails(): void {
		const sectionNameColumn: Column = {
			key: 'sectionName',
			name: 'Class Name',
			sortOrder: 'ascend',
			sortFn: (a: StrideSchoolSectionData, b: StrideSchoolSectionData) =>
				a.sectionName.localeCompare(b.sectionName),
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.rows().map(row => ({ text: row.sectionName, value: row.sectionName })),
			filterFn: (list: string[], item: StrideSchoolSectionData) =>
				list.some(name => item.sectionName.indexOf(name) !== -1),
		};

		const teacherColumn: Column = {
			key: 'teacher',
			name: 'Teacher',
		};

		const listeningColumn: Column = {
			key: 'listening',
			name: 'Listening',
		};

		const readingColumn: Column = {
			key: 'reading',
			name: 'Reading',
		};

		const speakingColumn: Column = {
			key: 'speaking',
			name: 'Speaking',
		};

		const writingColumn: Column = {
			key: 'writing',
			name: 'Writing',
		};

		const compositeColumn: Column = {
			key: 'composite',
			name: 'Composite',
		};

		const activitiesColumn: Column = {
			key: 'activities',
			name: 'Activities',
			sortOrder: null,
			sortFn: (a: StrideSchoolSectionData, b: StrideSchoolSectionData) =>
				a.activityCount.toString().localeCompare(b.activityCount.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		this.wideColumns = [
			sectionNameColumn,
			teacherColumn,
			listeningColumn,
			readingColumn,
			speakingColumn,
			writingColumn,
			compositeColumn,
			activitiesColumn,
		];

		this.narrowColumns = [sectionNameColumn, compositeColumn, activitiesColumn];

		this.columns.set(this.wideColumns);
	}

	public async onRowClick(row: StrideSchoolSectionData) {
		this.nav.navigateWithMatrix$(['/', 'my', 'reports'], { sectionUuid: row.sectionUuid });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
